/**
 * CompanyDashboard
 *
 * Top-level dashboard for Objectives, Initiatives, and Milestones. Orchestrates filters
 * (status, years, department), loads reference data, and coordinates drawers for
 * objective and initiative details. Supports deep linking via query params:
 * - dashboardTab: "Objectives" | "Initiatives"
 * - objectiveId | keyresultId | initiativeId
 *
 * Child modules: CompanyDashboardFilter, CompanyDashboardAllObjective,
 * CompanyDashboardInitiative, UpcomingMilestones.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { useSetLoading } from "../../common/hooks/appearance";
import {
  getAllStatus,
  getUpcomingMilestonesUpdated,
  getAllEmployees,
  getAllStatusForKeyResults,
  getAllStatusForInitiatives,
  getAllDepartments,
  getAllYearsForObjectives,
  getAllYearsForInitiatives,
} from "../../api/dashboard";
import { routes } from "../../constants/routes";
import UpcomingMilestones from "./upcoming-milestones/upcoming-milestones";
import CompanyDashboardFilter from "./company-dashboard-filter/company-dashboard-filter";
import CompanyDashboardAllObjective from "./company-dashboard-all-objective/company-dashboard-all-objective";
import CompanyDashboardInitiative from "./company-dashboard-initiative/company-dashboard-initiative";

import "./company-dashboard.scss";
import { usePermissions } from "../../common/hooks/usePermissions";
import MODULES from "../../constants/modules";
import { Typography } from "@mui/material";
import { getCurrentUser } from "../../common/routines/user-operations";
import NoRole from "../no-role/no-role";

const CompanyDashboard: React.FC = () => {
  const [objective, setObjective] = useState("Objectives");
  const [status, setStatus] = useState<any>([]);
  const [objectiveYear, setObjectiveYear] = useState<any[]>([]);
  const [initiativeYear, setInitiativeYear] = useState<any[]>([]);
  const [selectedObjectiveYear, setSelectedObjectiveYear] = useState("");
  const [selectedInitiativeYear, setSelectedInitiativeYear] = useState("");
  const [currentMonth, setCurrentMonth] = React.useState<Dayjs>(dayjs());
  const [loading, setLoading] = useState<boolean>(false);
  const [isAllStatusForKeyResultsLoaded, setIsAllStatusForKeyResultsLoaded] =
    useState(false);
  const [isAllStatusForInitiativesLoaded, setIsAllStatusForInitiativesLoaded] =
    useState(false);
  const [isAllStatusLoaded, setIsAllStatusLoaded] = useState(false);
  const [isAllMilestonesLoaded, setIsAllMilestonesLoaded] = useState(false);
  const [isAllObjectivesLoaded, setIsAllObjectivesLoaded] = useState(false);
  const [departments, setDepartments] = useState<any>([]);
  const [isShowInitiativeTableDrawer, setIsShowInitiativeTableDrawer] =
    useState(false);
  const [isShowAddInitiativeTableDrawer, setIsShowAddInitiativeTableDrawer] =
    useState(false);
  const [isShowAllObjectiveDrawer, setIsShowAllObjectiveDrawer] =
    useState(false);
  const [isShowAddAllObjectiveDrawer, setIsShowAddAllObjectiveDrawer] =
    useState(false);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [statusListKeyResults, setStatusListKeyResults] = useState<any[]>([]);
  const [statusListInitiatives, setStatusListInitiatives] = useState<any[]>([]);
  const [employeesList, setEmployeesList] = useState<any[]>([]);
  const [milestonesList, setMilestonesList] = useState<any[]>([]);
  const [departmentsList, setDepartmentsList] = useState<any[]>([]);
  const [initiativeData, setInitiativeData] = useState<any>([]);
  const [initiativeId, setInitiativeId] = useState<any>(null);
  const [objectiveId, setObjectiveId] = useState<any>([]);
  const [keyResultId, setKeyResultId] = useState<any>([]);
  const [isFromObjective, setIsFromObjective] = useState<any>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const objectiveIDParams = searchParams.get("objectiveID") || "";
  const initiativeIDParams = searchParams.get("initiativeID") || "";
  const dashboardTab = searchParams.get("dashboardTab");
  const objectiveIdFromParams = searchParams.get("objectiveId");
  const keyresultIdFromParams = searchParams.get("keyresultId");
  const initiativeIdFromParams = searchParams.get("initiativeId");

  const { canRead: canReadObjectives } = usePermissions(MODULES.OBJECTIVES);
  const { canRead: canReadInitiatives } = usePermissions(MODULES.INITIATIVES);
  const { canRead: canReadMilestones } = usePermissions(MODULES.MILESTONE);

  useEffect(() => {
    if (dashboardTab && objectiveIdFromParams) {
      setObjective(dashboardTab);
      setObjectiveId([Number(objectiveIdFromParams)]);
      setIsShowAllObjectiveDrawer(true);
    } else if (dashboardTab && keyresultIdFromParams) {
      setObjective(dashboardTab);
      setObjectiveId([Number(objectiveIdFromParams)]);
      setKeyResultId([Number(keyresultIdFromParams)]);
      setIsShowAllObjectiveDrawer(true);
    } else if (dashboardTab && initiativeIdFromParams) {
      setObjective(dashboardTab);
      setInitiativeId(initiativeIdFromParams);
      setIsShowAddInitiativeTableDrawer(true);
    } else if (objectiveIDParams) {
      setObjectiveId([Number(objectiveIDParams)]);
      setIsShowAllObjectiveDrawer(true);
    } else if (initiativeIDParams) {
      setInitiativeId(initiativeIDParams);
      setIsShowAddInitiativeTableDrawer(true);
      setObjective("Initiatives");
    }
  }, [
    objectiveIDParams,
    initiativeIDParams,
    dashboardTab,
    objectiveIdFromParams,
    keyresultIdFromParams,
    initiativeIdFromParams,
  ]);

  // Debug useEffect to track when selectedObjectiveYear changes
  useEffect(() => {}, [selectedObjectiveYear]);

  const handleAddObjective = () => {
    setIsShowAddAllObjectiveDrawer(true);
  };

  const handleAddInitiative = () => {
    setIsShowAddInitiativeTableDrawer(true);
  };

  const fetchAllStatus = async () => {
    setIsAllStatusLoaded(true);
    try {
      const response = await getAllStatus();
      setStatusList(response?.data?.data || []);
      localStorage.setItem(
        "statusList",
        JSON.stringify(response?.data?.data || []),
      );
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsAllStatusLoaded(false);
    }
  };
  const fetchAllStatusForKeyResults = async () => {
    setIsAllStatusForKeyResultsLoaded(true);
    try {
      const response = await getAllStatusForKeyResults();
      setStatusListKeyResults(response?.data?.data || []);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsAllStatusForKeyResultsLoaded(false);
    }
  };
  const fetchAllStatusForInitiatives = async () => {
    setIsAllStatusForInitiativesLoaded(true);
    try {
      const response = await getAllStatusForInitiatives();
      setStatusListInitiatives(response?.data?.data || []);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsAllStatusForInitiativesLoaded(false);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await getAllEmployees({});
      setEmployeesList(response?.data?.data || []);
      localStorage.setItem(
        "employeesList",
        JSON.stringify(response?.data?.data || []),
      );
    } catch (error: any) {
      console.error(error);
    }
  };
  const fetchAllDepartments = async () => {
    try {
      const response = await getAllDepartments();
      setDepartmentsList(response?.data?.data || []);
      localStorage.setItem(
        "departmentsList",
        JSON.stringify(response?.data?.data || []),
      );
    } catch (error: any) {
      console.error(error);
    }
  };

  const fetchUpcomingMilestones = async (payload: any) => {
    setIsAllMilestonesLoaded(true);

    try {
      const response = await getUpcomingMilestonesUpdated(payload);
      setMilestonesList(response?.data?.data || []);
    } catch (error: any) {
      console.error(error);
      setMilestonesList([]);
    } finally {
      setIsAllMilestonesLoaded(false);
    }
  };
  const fetchAllYearsForObjectives = async () => {
    setIsAllObjectivesLoaded(true);
    try {
      const response = await getAllYearsForObjectives();
      const years = response?.data?.data || [];
      setObjectiveYear(years);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsAllObjectivesLoaded(false);
    }
  };
  const fetchAllYearsForInitiatives = async () => {
    setIsAllStatusForInitiativesLoaded(true);
    try {
      const response = await getAllYearsForInitiatives();
      const years = response?.data?.data || [];
      setInitiativeYear(years);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsAllStatusForInitiativesLoaded(false);
    }
  };

  const handleObjectiveYearChange = (year: string) => {
    setSelectedObjectiveYear(year);
  };

  const handleInitiativeYearChange = (year: string) => {
    setSelectedInitiativeYear(year);
  };

  useEffect(() => {
    fetchAllEmployees();
    fetchAllDepartments();

    if (canReadObjectives) {
      fetchAllStatus();
      fetchAllStatusForKeyResults();
      fetchAllYearsForObjectives();
    }

    if (canReadInitiatives) {
      fetchAllStatusForInitiatives();
      fetchAllYearsForInitiatives();
    }
  }, [canReadObjectives, canReadInitiatives]);

  useEffect(() => {
    if (canReadMilestones) {
      fetchUpcomingMilestones({});
    } else {
      setIsAllMilestonesLoaded(false);
    }
  }, [canReadMilestones]);

  const showLoading = useMemo(
    () =>
      loading ||
      isAllStatusForKeyResultsLoaded ||
      isAllStatusLoaded ||
      isAllStatusForInitiativesLoaded ||
      isAllMilestonesLoaded ||
      isAllObjectivesLoaded,
    [
      loading,
      isAllStatusForKeyResultsLoaded,
      isAllStatusLoaded,
      isAllStatusForInitiativesLoaded,
      isAllMilestonesLoaded,
      isAllObjectivesLoaded,
    ],
  );
  useSetLoading(showLoading);

  const handleShowAddInitiativeDrawer = (show: boolean) => {
    setIsShowAddInitiativeTableDrawer(show);
    if (!show) {
      setSearchParams({});
    }
  };

  const handleShowAllObjectiveDrawer = (show: boolean) => {
    setIsShowAllObjectiveDrawer(show);
    if (!show) {
      setSearchParams({});
    }
  };

  const userDetails = getCurrentUser();
  if (userDetails?.roles?.length === 0 && !showLoading) {
    return <NoRole isNoRole={true} />;
  }

  const renderMainContent = () => {
    if (objective === "Objectives" && canReadObjectives) {
      return (
        <CompanyDashboardAllObjective
          key={objectiveId}
          isShowAllObjectiveDrawer={isShowAllObjectiveDrawer}
          setIsShowAllObjectiveDrawer={handleShowAllObjectiveDrawer}
          isShowAddAllObjectiveDrawer={isShowAddAllObjectiveDrawer}
          setIsShowAddAllObjectiveDrawer={setIsShowAddAllObjectiveDrawer}
          setAlertMessage={() => {}}
          setAlertType={() => {}}
          setAlertOpen={() => {}}
          setLoading={setLoading}
          year={selectedObjectiveYear}
          status={status}
          departments={departments}
          setDepartments={setDepartments}
          employeesList={employeesList}
          statusListKeyResults={statusListKeyResults}
          statusListObjectives={statusList}
          statusListInitiatives={statusListInitiatives}
          departmentsList={departmentsList}
          objectiveId={objectiveId}
          keyResultId={keyResultId}
          loading={loading}
          fetchUpcomingMilestones={fetchUpcomingMilestones}
          isShowMessageLoading={showLoading}
          fetchAllYearsForObjectives={fetchAllYearsForObjectives}
          fetchAllYearsForInitiatives={fetchAllYearsForInitiatives}
        />
      );
    }

    if (objective === "Initiatives" && canReadInitiatives) {
      return (
        <CompanyDashboardInitiative
          isShowInitiativeTableDrawer={isShowInitiativeTableDrawer}
          setIsShowInitiativeTableDrawer={setIsShowInitiativeTableDrawer}
          isShowAddInitiativeTableDrawer={isShowAddInitiativeTableDrawer}
          setIsShowAddInitiativeTableDrawer={handleShowAddInitiativeDrawer}
          setAlertMessage={() => {}}
          setAlertType={() => {}}
          setAlertOpen={() => {}}
          setLoading={setLoading}
          year={selectedInitiativeYear}
          status={status}
          departments={departments}
          setDepartments={setDepartments}
          employeesList={employeesList}
          statusListKeyResults={statusListKeyResults}
          statusListObjectives={statusList}
          statusListInitiatives={statusListInitiatives}
          initiativeId={initiativeId}
          setInitiativeId={setInitiativeId}
          objectiveId={objectiveId}
          setObjectiveId={setObjectiveId}
          keyResultId={keyResultId}
          setKeyResultId={setKeyResultId}
          setInitiativeData={setInitiativeData}
          initiativeData={initiativeData}
          fetchUpcomingMilestones={fetchUpcomingMilestones}
          isFromObjective={isFromObjective}
          setIsFromObjective={setIsFromObjective}
          setObjective={setObjective}
          setIsShowAddAllObjectiveDrawer={setIsShowAddAllObjectiveDrawer}
          departmentsList={departmentsList}
          isShowMessageLoading={showLoading}
          fetchAllYearsForInitiatives={fetchAllYearsForInitiatives}
          fetchAllYearsForObjectives={fetchAllYearsForObjectives}
        />
      );
    }

    if (objective === "Upcoming Milestones" && canReadMilestones) {
      return (
        <div className="company-dashboard-content-tab-section-left company-dashboard-content-tab-section-left-mobile">
          <UpcomingMilestones
            milestonesList={milestonesList}
            setMilestonesList={setMilestonesList}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            initiativeId={initiativeId}
            setInitiativeId={setInitiativeId}
            objectiveId={objectiveId}
            setObjectiveId={setObjectiveId}
            keyResultId={keyResultId}
            setKeyResultId={setKeyResultId}
            setObjective={setObjective}
            isShowAddInitiativeTableDrawer={isShowAddInitiativeTableDrawer}
            setIsShowAddInitiativeTableDrawer={handleShowAddInitiativeDrawer}
            setIsFromObjective={setIsFromObjective}
            objective={objective}
          />
        </div>
      );
    }
    return (
      <Typography className="no-access-text">
        No access to this section
      </Typography>
    );
  };

  const renderSidebarContent = () => {
    if (canReadMilestones) {
      return (
        <div className="company-dashboard-content-tab-section-left company-dashboard-content-tab-section-left-web">
          <UpcomingMilestones
            milestonesList={milestonesList}
            setMilestonesList={setMilestonesList}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            initiativeId={initiativeId}
            setInitiativeId={setInitiativeId}
            objectiveId={objectiveId}
            setObjectiveId={setObjectiveId}
            keyResultId={keyResultId}
            setKeyResultId={setKeyResultId}
            setObjective={setObjective}
            isShowAddInitiativeTableDrawer={isShowAddInitiativeTableDrawer}
            setIsShowAddInitiativeTableDrawer={handleShowAddInitiativeDrawer}
            setIsFromObjective={setIsFromObjective}
            objective={objective}
          />
        </div>
      );
    }

    return (
      <Typography className="no-access-text">
        No access to Milestone section
      </Typography>
    );
  };

  return (
    <div className="company-dashboard">
      <div className="company-dashboard-content">
        <div className="company-dashboard-content-tab">
          <div className="company-dashboard-content-tab-container">
            <button className="company-dashboard-content-tab-btn company-dashboard-content-tab-active">
              Company
            </button>
            <button
              className="company-dashboard-content-tab-btn"
              onClick={() => {
                navigate(routes.REQUESTDASHBOARD);
              }}
            >
              Requests
            </button>
          </div>
        </div>
        <div className="company-dashboard-content-tab-section">
          <div className="company-dashboard-content-tab-section-right">
            <CompanyDashboardFilter
              objective={objective}
              status={status}
              objectiveYear={objectiveYear}
              initiativeYear={initiativeYear}
              setObjective={setObjective}
              setStatus={setStatus}
              onObjectiveYearChange={handleObjectiveYearChange}
              onInitiativeYearChange={handleInitiativeYearChange}
              handleAddObjective={handleAddObjective}
              handleAddInitiative={handleAddInitiative}
              statusList={statusList}
              departmentsList={departmentsList}
              departments={departments}
              setDepartments={setDepartments}
              objectiveId={objectiveId}
              handleShowAllObjectiveDrawer={handleShowAllObjectiveDrawer}
              handleShowAddInitiativeDrawer={handleShowAddInitiativeDrawer}
            />
            {renderMainContent()}
          </div>
          {renderSidebarContent()}
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
